import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import { collection, query, orderBy, limit, getDocs, where, Timestamp, getCountFromServer } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Search, TrendingUp, Clock, Calendar, BarChart2, Activity, Filter } from 'lucide-react';
import './SearchAnalytics.css';

function SearchAnalytics() {
  const [trendingSearches, setTrendingSearches] = useState([]);
  const [userSearchHistory, setUserSearchHistory] = useState([]);
  const [searchStats, setSearchStats] = useState({
    todayCount: 0,
    weekCount: 0,
    totalCount: 0,
    popularTags: []
  });
  const [timeFilter, setTimeFilter] = useState('week');
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  // Get current user
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUser(user);
      if (user) {
        fetchUserSearchHistory(user.uid);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch user search history
  const fetchUserSearchHistory = async (userId) => {
    if (!userId) return;
    
    try {
      const searchesRef = collection(db, 'searchQueries');
      const userSearchQuery = query(
        searchesRef,
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(10)
      );
      
      const snapshot = await getDocs(userSearchQuery);
      
      const history = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          query: data.query,
          timestamp: data.timestamp?.toDate() || new Date(),
          resultCount: data.resultCount || 0
        };
      });
      
      setUserSearchHistory(history);
    } catch (error) {
      console.error('Error fetching user search history:', error);
    }
  };

  // Load trending searches
  useEffect(() => {
    const fetchTrendingSearches = async () => {
      try {
        setIsLoading(true);
        const searchesRef = collection(db, 'searchQueries');
        const dateLimit = getDateLimit(timeFilter);
        
        // Ensure there's an index for this query in Firebase
        let searchQuery = query(
          searchesRef,
          where('timestamp', '>=', dateLimit),
          orderBy('timestamp', 'desc')
        );
        
        const snapshot = await getDocs(searchQuery);
        
        if (snapshot.empty) {
          setTrendingSearches([]);
          setIsLoading(false);
          return;
        }
        
        // Count occurrences of each search term
        const searchCounts = {};
        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.query) {
            const term = data.query.toLowerCase().trim();
            if (term) {
              searchCounts[term] = (searchCounts[term] || 0) + 1;
            }
          }
        });
        
        // Convert to array, sort by count
        const trending = Object.entries(searchCounts)
          .map(([term, count]) => ({ term, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);
          
        setTrendingSearches(trending);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching trending searches:', error);
        setIsLoading(false);
      }
    };
    
    fetchTrendingSearches();
  }, [timeFilter]);

  // Fetch search statistics
  useEffect(() => {
    const fetchSearchStats = async () => {
      try {
        setIsLoading(true);
        const searchesRef = collection(db, 'searchQueries');
        
        // Today's searches
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayTimestamp = Timestamp.fromDate(today);
        
        const todayQuery = query(
          searchesRef,
          where('timestamp', '>=', todayTimestamp)
        );
        
        // This week's searches
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const weekTimestamp = Timestamp.fromDate(weekAgo);
        
        const weekQuery = query(
          searchesRef,
          where('timestamp', '>=', weekTimestamp)
        );
        
        // Use getCountFromServer instead of fetching all documents
        const [todayCount, weekCount, totalCount] = await Promise.all([
          getCountFromServer(todayQuery),
          getCountFromServer(weekQuery),
          getCountFromServer(collection(db, 'searchQueries'))
        ]);
        
        // Fetch data for tags extraction
        const weekSnapshot = await getDocs(weekQuery);
        
        // Extract tags from search data
        const tagsMap = {};
        weekSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.tags && Array.isArray(data.tags)) {
            data.tags.forEach(tag => {
              tagsMap[tag] = (tagsMap[tag] || 0) + 1;
            });
          }
        });
        
        const popularTags = Object.entries(tagsMap)
          .map(([tag, count]) => ({ tag, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);
        
        setSearchStats({
          todayCount: todayCount.data().count,
          weekCount: weekCount.data().count,
          totalCount: totalCount.data().count,
          popularTags
        });
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching search statistics:', error);
        setIsLoading(false);
      }
    };
    
    fetchSearchStats();
  }, []);

  // Helper function to get a date limit based on the time filter
  const getDateLimit = (filter) => {
    const date = new Date();
    switch (filter) {
      case 'day':
        date.setHours(0, 0, 0, 0);
        break;
      case 'week':
        date.setDate(date.getDate() - 7);
        break;
      case 'month':
        date.setMonth(date.getMonth() - 1);
        break;
      case 'year':
        date.setFullYear(date.getFullYear() - 1);
        break;
      default:
        date.setDate(date.getDate() - 7);
    }
    return Timestamp.fromDate(date);
  };

  // Handle search click
  const handleSearchClick = (term) => {
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  return (
    <div className="search-analytics-container">
      <Navbar />
      <div className="search-analytics-content">
        <div className="search-analytics-header">
          <h1>Search Analytics</h1>
          <p>Discover trending searches and your search history</p>
        </div>
        
        <div className="analytics-grid">
          {/* Stats Cards */}
          <div className="stats-section">
            <h2><BarChart2 size={20} /> Search Statistics</h2>
            <div className="stats-cards">
              <div className="stat-card">
                <div className="stat-icon today">
                  <Calendar size={20} />
                </div>
                <div className="stat-info">
                  <h3>Today</h3>
                  <p className="stat-number">{searchStats.todayCount}</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon week">
                  <Activity size={20} />
                </div>
                <div className="stat-info">
                  <h3>This Week</h3>
                  <p className="stat-number">{searchStats.weekCount}</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon total">
                  <Search size={20} />
                </div>
                <div className="stat-info">
                  <h3>All Time</h3>
                  <p className="stat-number">{searchStats.totalCount}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Trending Searches */}
          <div className="trending-section">
            <div className="section-header">
              <h2><TrendingUp size={20} /> Trending Searches</h2>
              <div className="filter-controls">
                <Filter size={16} />
                <select 
                  value={timeFilter} 
                  onChange={(e) => setTimeFilter(e.target.value)}
                  className="time-filter"
                >
                  <option value="day">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="year">This Year</option>
                </select>
              </div>
            </div>
            
            {isLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading trending searches...</p>
              </div>
            ) : trendingSearches.length > 0 ? (
              <div className="trending-searches">
                {trendingSearches.map((search, index) => (
                  <div 
                    key={index} 
                    className="trending-search-item"
                    onClick={() => handleSearchClick(search.term)}
                  >
                    <div className="trend-rank">{index + 1}</div>
                    <div className="trend-term">{search.term}</div>
                    <div className="trend-count">{search.count} searches</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No trending searches found for the selected time period.</p>
              </div>
            )}
          </div>
          
          {/* User Search History */}
          <div className="history-section">
            <h2><Clock size={20} /> Your Recent Searches</h2>
            
            {!currentUser ? (
              <div className="empty-state">
                <p>Please sign in to view your search history.</p>
              </div>
            ) : userSearchHistory.length > 0 ? (
              <div className="search-history">
                {userSearchHistory.map((search, index) => (
                  <div 
                    key={index} 
                    className="history-item"
                    onClick={() => handleSearchClick(search.query)}
                  >
                    <div className="history-query">{search.query}</div>
                    <div className="history-meta">
                      <span className="history-time">{search.timestamp.toLocaleString()}</span>
                      <span className="history-results">{search.resultCount} results</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>You haven't made any searches yet.</p>
              </div>
            )}
          </div>
          
          {/* Popular Tags */}
          <div className="tags-section">
            <h2><TrendingUp size={20} /> Popular Tags in Searches</h2>
            
            {isLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading popular tags...</p>
              </div>
            ) : searchStats.popularTags.length > 0 ? (
              <div className="popular-tags">
                {searchStats.popularTags.map((tag, index) => (
                  <div 
                    key={index} 
                    className="popular-tag"
                    onClick={() => navigate(`/search?tags=${encodeURIComponent(tag.tag)}`)}
                  >
                    <span className="tag-name">{tag.tag}</span>
                    <span className="tag-count">{tag.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No popular tags found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SearchAnalytics; 